import { Component, Injector, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { authState } from '@angular/fire/auth';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, switchMap, take } from 'rxjs/operators';
import { MfaService } from '../../../../../aumico-lib-users-fe/src/lib/services/mfa.service';
import { Paths } from '../../enums/paths.enum';
import { BasePageComponent } from '../base.page';

@UntilDestroy()
@Component({
    selector: 'app-page-mfa-verify',
    templateUrl: './mfa-verify.page.html',
    styleUrls: ['./mfa-verify.page.scss'],
})
export class MfaVerifyPage extends BasePageComponent implements OnInit {
    isLoading = false;
    errorMessage: string | null = null;
    useBackupCode = false;
    userEmail: string | null = null;
    organizationName: string | null = null;

    verifyForm = new UntypedFormGroup({
        code: new UntypedFormControl('', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]),
    });

    constructor(injector: Injector, private mfaService: MfaService) {
        super(injector);
    }

    override ngOnInit(): void {
        authState(this.auth).pipe(
            filter((user) => user !== null),
            take(1),
            switchMap((user) => {
                this.userEmail = user!.email ?? null;
                return this.mfaService.getCurrentOrganizationName$();
            }),
            untilDestroyed(this),
        ).subscribe((name) => (this.organizationName = name));
    }

    toggleBackupCode(): void {
        this.useBackupCode = !this.useBackupCode;
        this.verifyForm.reset();
        this.errorMessage = null;
        if (this.useBackupCode) {
            this.verifyForm.controls['code'].setValidators([Validators.required]);
        } else {
            this.verifyForm.controls['code'].setValidators([Validators.required, Validators.minLength(6), Validators.maxLength(6)]);
        }
        this.verifyForm.controls['code'].updateValueAndValidity();
    }

    onVerify(): void {
        if (this.verifyForm.invalid) return;
        this.isLoading = true;
        this.errorMessage = null;
        const code = (this.verifyForm.value.code as string).trim();
        this.mfaService.verify$(code)
            .pipe(
                switchMap(() => this.mfaService.forceTokenRefresh$()),
                untilDestroyed(this),
            )
            .subscribe({
                next: () => {
                    this.authProvider.dispatchGetCurrentUser();
                    this.authProvider.getCurrentUser$()
                        .pipe(untilDestroyed(this))
                        .subscribe((user) => {
                            if (user) {
                                const returnUrl = this.safeReturnUrl();
                                if (returnUrl != null) {
                                    void this.router.navigateByUrl(returnUrl);
                                    return;
                                }
                                const lang = this.getCurrentLanguageWithFallBack();
                                this.router.navigate([`/${lang}/${Paths.HOME}`]);
                            }
                        });
                    this.isLoading = false;
                },
                error: () => {
                    this.isLoading = false;
                    this.errorMessage = 'aumico_mfa_verify_error_invalid_code';
                },
            });
    }
}
